import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import { Primary } from "./";
import config from "@lib/config";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const drawerWidth = config.drawerWidth;
const APP_BAR_HEIGHT = 70;
const STATUS_BAR_HEIGHT = 42;

interface DrawerStyleProps {
  open: boolean;
  statusBarOpen: boolean;
}

interface MainProps extends DrawerStyleProps {
  toggleDrawer: () => void;
}

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "statusBarOpen",
})<DrawerStyleProps>(
  ({ theme, open, statusBarOpen }) => ({
    position: "fixed",
    // Anchor below the complete fixed header stack. The status banner adds
    // 42px above the 70px app bar, so the drawer must move with the app bar
    // instead of continuing to paint over its lower edge.
    top: `${APP_BAR_HEIGHT + (statusBarOpen ? STATUS_BAR_HEIGHT : 0)}px`,
    left: 0,
    bottom: 0,
    transition: "top 0.8s ease",
    // The AppBar sits at theme.zIndex.drawer + 1 so it paints above the
    // permanent drawer where they abut below the header — but that also put
    // it above the toggle button (which only wins comparisons *within* the
    // drawer's own stacking context), clipping/hiding it on pages where the
    // button's calculated vertical position lands near that seam. Push the
    // whole drawer above the AppBar instead; nothing here needs to render
    // underneath it.
    zIndex: theme.zIndex.drawer + 2,
    "& .MuiDrawer-paper": {
      overflow: "visible",
      borderRight: "none",
      position: "relative",
      // Paper only sizes itself to its content (the nav items) by default,
      // not the full sidebar height — below the last item, the page
      // content behind the (fixed-position) drawer shows through. Force it
      // to fill the drawer's own height and paint an opaque background the
      // whole way down.
      height: "100%",
      backgroundColor: "#ffffff",
      width: drawerWidth,
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: "border-box",
      ...(!open && {
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: "77px",
      }),
    },
    "--nav-icon-size": open ? "var(--icon-size-sm)" : "var(--icon-size-md)",
    "--nav-item-padding-y": open ? "var(--space-2)" : "var(--space-3)",
    ".MuiListItemIcon-root": {
      minWidth: "auto",
      marginRight: "var(--space-2)",
    },
    ".MuiListItemText-root": {
      display: open ? "block" : "none",
    },
    ".MuiListSubheader-root": {
      display: open ? "block" : "none",
    },
    ".MuiTypography-root": {
      display: open ? "block" : "none",
    },
  })
);

const ToggleButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "calc(50% - 34px)",
  right: "-10px",
  width: "20px",
  height: "34px",
  boxShadow: theme.shadows[1],
  zIndex: "1300",
  borderRadius: "3px",
  backgroundColor: "white",

  "&:hover": {
    backgroundColor: theme.palette.grey[200],
  },
}));

const NavList = styled(List)(({ theme }) => ({
  borderRight: "1px solid",
  borderColor: theme.palette.grey[300],
  height: "100%",
  paddingTop: 0,
}));

export default function Main({ open, statusBarOpen, toggleDrawer }: MainProps) {
  return (
    <Drawer variant="permanent" open={open} statusBarOpen={statusBarOpen}>
      <ToggleButton onClick={toggleDrawer}>
        {open && <ChevronLeftIcon sx={{ color: (theme) => theme.palette.grey[700] }} />}
        {!open && <ChevronRightIcon sx={{ color: (theme) => theme.palette.grey[700] }} />}
      </ToggleButton>
      <NavList as="nav">
        <Primary open={open} />
      </NavList>
    </Drawer>
  );
}
